import { ref, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import useLocale from '../composables/useLocale';
import useNavigationStore from '../stores/navigation';
import useRenditionStore from '../stores/rendition';
import useReaderStore from '../stores/reader';
import usePlaylistStore from '../stores/playlist';
import usePositionTrackingStore from '../stores/position-tracking';
import {
  calculateAudioOnlyPageRange,
  calculateScrubbingPageRange,
  calculateFallbackPageRange,
  calculateStandardPageRange,
  type PageRangeData,
} from '../util/page-range-calculators';
import useUiStore from '../stores/ui';

const useBookPagination = () => {
  const { t } = useLocale();
  const { isReflowable, isItemShownOnPage, isSpread } = storeToRefs(useRenditionStore());
  const navStore = useNavigationStore();
  const { totalSpineItems, hasPages, pages, scrubPageIndex, isScrubbing, currentPageIndex, currentPositionPct } =
    storeToRefs(navStore);
  const { getChapterTitleForPageIndex, getChapterTitleForHref } = navStore;
  const { isContinuous, isAudioOnly } = storeToRefs(useReaderStore());
  const { playlist, totalPlaylistItems } = storeToRefs(usePlaylistStore());
  const { currentChapterTitle, currentPageIndex: currentPositionIndex } = storeToRefs(usePositionTrackingStore());
  const { isTimeBasedSeekingVisible } = storeToRefs(useUiStore());

  const scrubEndTimestamp = ref(0);
  const totalPageCount = computed(() => pages.value[pages.value.length - 1]?.page || pages.value.length);

  /**
   * Helper to get page number for playlist index
   */
  const getPageNumberForPlaylistIndex = (playlistIdx: number): number | null => {
    const item = playlist.value[playlistIdx];
    if (!item || !pages.value.length) return null;

    const matchingPage = pages.value.find((page) => {
      const cleanPageHref = page.href.split('#')[0];
      const cleanItemHref = item.href.split('#')[0];
      return cleanPageHref === cleanItemHref || cleanItemHref.includes(cleanPageHref);
    });

    return matchingPage?.page || null;
  };

  /**
   * Formats the page range data into localized text
   */
  const formatPageRangeText = (data: PageRangeData): string => {
    if (data.isPercentage) {
      return `${data.to}%`;
    }

    if (data.isTimeBasedSeeking) {
      return t('book_player.label_segment', { n: data.to });
    }

    if (data.isSegment) {
      return t('book_player.label_segment_to_of_total', { to: data.to, total: data.total });
    }

    if (data.from !== undefined && data.from !== data.to) {
      return t('book_player.label_pages_from_to_of_total', { from: data.from, to: data.to, total: data.total });
    }

    return t('book_player.label_page_to_of_total', { to: data.to, total: data.total });
  };

  watch(isScrubbing, (newValue, oldValue) => {
    if (oldValue && !newValue) scrubEndTimestamp.value = Date.now();
  });

  /**
   * Main page range computed - delegates to appropriate calculator
   */
  const pageRange = computed(() => {
    // AUDIO-ONLY MODE
    if (isAudioOnly.value) {
      const isRecentlyScrubbed = Date.now() - scrubEndTimestamp.value < 300;
      const activeScrubIdx = scrubPageIndex.value;
      const isActivelyScrubbing = isScrubbing.value || isRecentlyScrubbed;
      const displayIndex = isActivelyScrubbing && activeScrubIdx !== null ? activeScrubIdx : currentPositionIndex.value;

      const pageNum = getPageNumberForPlaylistIndex(displayIndex);

      const item = playlist.value[displayIndex];
      const chapter = item ? getChapterTitleForHref(item.href) : currentChapterTitle.value;

      const data = calculateAudioOnlyPageRange({
        displayIndex,
        pageNum,
        totalPages: totalPageCount.value,
        totalPlaylistItems: totalPlaylistItems.value,
        chapter,
        isTimeBasedSeeking: isTimeBasedSeekingVisible.value,
      });

      if (chapter && !pageNum && !data.isTimeBasedSeeking) {
        return { chapter: data.chapter };
      }

      return {
        text: formatPageRangeText(data),
        chapter: data.chapter,
      };
    }

    // SCRUBBING & GRACE PERIOD
    const isRecentlyScrubbed = Date.now() - scrubEndTimestamp.value < 300;
    const activeScrubIdx = scrubPageIndex.value;

    if (
      (isScrubbing.value && hasPages.value && !isContinuous.value) ||
      (isRecentlyScrubbed && activeScrubIdx !== null)
    ) {
      const idx = activeScrubIdx ?? currentPageIndex.value;
      const pageNum = pages.value[idx]?.page || 0;

      const data = calculateScrubbingPageRange({
        pageNum,
        totalPageCount: totalPageCount.value,
        chapter: getChapterTitleForPageIndex(idx),
      });

      return {
        text: formatPageRangeText(data),
        chapter: data.chapter,
      };
    }

    // NO PAGE LIST (Fallback)
    if (!hasPages.value) {
      const activeScrubIdx = scrubPageIndex.value;
      const active = isScrubbing.value ? (activeScrubIdx ?? 0) : currentPositionPct.value;

      const data = calculateFallbackPageRange({
        active,
        isReflowable: isReflowable.value,
        totalSpineItems: totalSpineItems.value,
      });

      return {
        text: formatPageRangeText(data),
        chapter: data.chapter,
      };
    }

    // STANDARD VIEW
    const data = calculateStandardPageRange({
      isReflowable: isReflowable.value,
      isSpread: isSpread.value,
      isContinuous: isContinuous.value,
      currentPageIndex: currentPageIndex.value,
      pages: pages.value,
      totalPageCount: totalPageCount.value,
      isItemShownOnPage: isItemShownOnPage.value,
      getChapterTitleForPageIndex,
    });

    return {
      text: formatPageRangeText(data),
      chapter: data.chapter,
    };
  });

  return { pageRange };
};

export default useBookPagination;
